import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Network, Server, Users, Database, 
  Shield, HardDrive, Router, BarChart3, Activity, Target, CheckCircle, AlertTriangle,
  Globe, Zap, Clock
} from 'lucide-react';
import { ResponsiveContainer, PieChart as RechartsPieChart, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Pie } from 'recharts';

// interface Asset removed for build optimization

interface User {
  id: string;
  name: string;
  role: string;
  department: string;
  accessLevel: 'admin' | 'user' | 'guest';
  applications: string[];
  devices: string[];
  lastLogin: string;
  status: 'active' | 'inactive' | 'suspended';
}

interface BackupPlan {
  id: string;
  name: string;
  frequency: string;
  retention: string;
  type: 'full' | 'incremental' | 'differential';
  location: string;
  lastBackup: string;
  nextBackup: string;
  status: 'success' | 'warning' | 'error';
  size: string;
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface CatalogueActifsTIProps {
  isOpen: boolean;
  onClose: () => void;
}

// Mock data removed for build optimization

const mockUsers: User[] = [
  {
    id: 'usr-001',
    name: 'Jean-Pierre Leclerc',
    role: 'Directeur Service TI',
    department: 'Direction',
    accessLevel: 'admin',
    applications: ['SAP Business One', 'Active Directory', 'Monitoring'],
    devices: ['Laptop Dell', 'iPhone', 'VPN Access'],
    lastLogin: '2024-01-20',
    status: 'active'
  },
  {
    id: 'usr-002',
    name: 'Marie Dubois',
    role: 'Comptable Senior',
    department: 'Finance',
    accessLevel: 'user',
    applications: ['SAP Business One', 'Office 365', 'SharePoint'],
    devices: ['Desktop HP', 'Printer'],
    lastLogin: '2024-01-19',
    status: 'active'
  },
  {
    id: 'usr-003',
    name: 'Pierre Martin',
    role: 'Développeur',
    department: 'IT',
    accessLevel: 'admin',
    applications: ['Visual Studio', 'SQL Server', 'Git', 'Azure DevOps'],
    devices: ['Laptop Lenovo', 'Dual Monitor'],
    lastLogin: '2024-01-20',
    status: 'active'
  }
];

const mockBackupPlans: BackupPlan[] = [
  {
    id: 'backup-001',
    name: 'Backup Complet Quotidien',
    frequency: 'Quotidien 02:00',
    retention: '30 jours',
    type: 'full',
    location: 'Datacenter Secondaire',
    lastBackup: '2024-01-19',
    nextBackup: '2024-01-20',
    status: 'success',
    size: '2.5TB'
  },
  {
    id: 'backup-002',
    name: 'Backup Incrémental Base de Données',
    frequency: 'Toutes les 4h',
    retention: '7 jours',
    type: 'incremental',
    location: 'Cloud Azure',
    lastBackup: '2024-01-20',
    nextBackup: '2024-01-20',
    status: 'success',
    size: '150GB'
  },
  {
    id: 'backup-003',
    name: 'Backup Fichiers Utilisateurs',
    frequency: 'Hebdomadaire',
    retention: '12 semaines',
    type: 'differential',
    location: 'NAS Local',
    lastBackup: '2024-01-15',
    nextBackup: '2024-01-22',
    status: 'warning',
    size: '800GB'
  }
];

const createSlides = (): Slide[] => [
  {
    id: 'overview',
    title: 'Vue d\'Ensemble des Actifs TI',
    subtitle: 'Inventaire complet et statut des actifs',
    icon: Network,
    color: '#0066FF',
    content: (
      <div className="space-y-6">
        {/* Métriques Globales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Server className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Serveurs</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">12</div>
            <div className="text-xs text-gray-500">Actifs</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Network className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Équipements Réseau</span>
            </div>
            <div className="text-2xl font-bold text-green-600">8</div>
            <div className="text-xs text-gray-500">Actifs</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-600">Utilisateurs</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">156</div>
            <div className="text-xs text-gray-500">Actifs</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">Applications</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">24</div>
            <div className="text-xs text-gray-500">Actifs</div>
          </div>
        </div>

        {/* Statut des Actifs */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Statut des Actifs par Catégorie
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { category: 'Serveurs', actifs: 12, critiques: 8, maintenance: 2 },
                { category: 'Réseau', actifs: 8, critiques: 6, maintenance: 1 },
                { category: 'Applications', actifs: 24, critiques: 12, maintenance: 3 },
                { category: 'Sécurité', actifs: 6, critiques: 4, maintenance: 0 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="actifs" fill="#0066FF" name="Actifs" />
                <Bar dataKey="critiques" fill="#E74C3C" name="Critiques" />
                <Bar dataKey="maintenance" fill="#F39C12" name="Maintenance" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Criticité des Actifs */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-red-600" />
            Répartition par Criticité
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={[
                    { name: 'Critique', value: 15, color: '#E74C3C' },
                    { name: 'Élevée', value: 25, color: '#F39C12' },
                    { name: 'Moyenne', value: 35, color: '#3498DB' },
                    { name: 'Faible', value: 25, color: '#27AE60' }
                  ]}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {[
                    { name: 'Critique', value: 15, color: '#E74C3C' },
                    { name: 'Élevée', value: 25, color: '#F39C12' },
                    { name: 'Moyenne', value: 35, color: '#3498DB' },
                    { name: 'Faible', value: 25, color: '#27AE60' }
                  ].map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'topology',
    title: 'Plan Topologie Réseau',
    subtitle: 'Architecture réseau et interconnexions',
    icon: Network,
    color: '#27AE60',
    content: (
      <div className="space-y-6">
        {/* Diagramme Topologie */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Network className="w-5 h-5 text-green-600" />
            Architecture Réseau
          </h3>
          <div className="bg-gray-50 p-8 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Internet */}
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800">Internet</h4>
                <p className="text-sm text-gray-600">1Gbps Symétrique</p>
              </div>

              {/* Firewall */}
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800">Firewall</h4>
                <p className="text-sm text-gray-600">Cisco ASA 5525-X</p>
              </div>

              {/* Core Switch */}
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Router className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800">Core Switch</h4>
                <p className="text-sm text-gray-600">Cisco Catalyst 9500</p>
              </div>
            </div>

            {/* Connexions */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center justify-center">
                <div className="w-full h-0.5 bg-gray-300"></div>
                <div className="px-4 text-sm text-gray-600">DMZ</div>
                <div className="w-full h-0.5 bg-gray-300"></div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Server className="w-6 h-6 text-white" />
                  </div>
                  <h5 className="font-medium text-gray-800">Serveurs Web</h5>
                  <p className="text-xs text-gray-600">DMZ</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Database className="w-6 h-6 text-white" />
                  </div>
                  <h5 className="font-medium text-gray-800">Serveurs DB</h5>
                  <p className="text-xs text-gray-600">LAN Sécurisé</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Spécifications Réseau */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" />
              Performance Réseau
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Bande passante</span>
                <span className="font-semibold">1Gbps</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Latence interne</span>
                <span className="font-semibold">&lt; 2ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Uptime</span>
                <span className="font-semibold text-green-600">99.9%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Redondance</span>
                <span className="font-semibold text-green-600">Active</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              Sécurité Réseau
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Segmentation VLAN</span>
                <span className="font-semibold text-green-600">✓</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Firewall Next-Gen</span>
                <span className="font-semibold text-green-600">✓</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">IPS/IDS</span>
                <span className="font-semibold text-green-600">✓</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monitoring 24/7</span>
                <span className="font-semibold text-green-600">✓</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'users',
    title: 'Catalogue des Utilisateurs',
    subtitle: 'Gestion des accès et dépendances utilisateurs',
    icon: Users,
    color: '#8E44AD',
    content: (
      <div className="space-y-6">
        {/* Métriques Utilisateurs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-600">Total Utilisateurs</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">156</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Actifs</span>
            </div>
            <div className="text-2xl font-bold text-green-600">142</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Administrateurs</span>
            </div>
            <div className="text-2xl font-bold text-red-600">8</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">Inactifs</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">14</div>
          </div>
        </div>

        {/* Utilisateurs par Département */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Répartition par Département
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { department: 'IT', users: 25, admins: 8 },
                { department: 'Finance', users: 18, admins: 2 },
                { department: 'RH', users: 12, admins: 1 },
                { department: 'Production', users: 45, admins: 3 },
                { department: 'Ventes', users: 32, admins: 2 },
                { department: 'Direction', users: 8, admins: 2 },
                { department: 'Marketing', users: 16, admins: 1 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="users" fill="#8E44AD" name="Utilisateurs" />
                <Bar dataKey="admins" fill="#E74C3C" name="Administrateurs" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Exemples d'Utilisateurs */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Profils Utilisateurs Types
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mockUsers.map((user, _index) => (
              <div key={user.id} className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                    user.accessLevel === 'admin' ? 'bg-red-500' : 'bg-blue-500'
                  }`}>
                    {user.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{user.name}</h4>
                    <p className="text-sm text-gray-600">{user.role}</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Département:</span>
                    <span className="font-medium">{user.department}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Niveau d'accès:</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.accessLevel === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.accessLevel}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dernière connexion:</span>
                    <span className="font-medium">{user.lastLogin}</span>
                  </div>
                </div>

                <div className="mt-3">
                  <h5 className="text-xs font-medium text-gray-700 mb-1">Applications:</h5>
                  <div className="flex flex-wrap gap-1">
                    {user.applications.slice(0, 2).map((app, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {app}
                      </span>
                    ))}
                    {user.applications.length > 2 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        +{user.applications.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'backup',
    title: 'Plan de Backup et Récupération',
    subtitle: 'Stratégie de sauvegarde et continuité des services',
    icon: HardDrive,
    color: '#E74C3C',
    content: (
      <div className="space-y-6">
        {/* Métriques Backup */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Plans de Backup</span>
            </div>
            <div className="text-2xl font-bold text-red-600">12</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Succès</span>
            </div>
            <div className="text-2xl font-bold text-green-600">95%</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Données Sauvegardées</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">3.5TB</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">RTO Moyen</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">4h</div>
          </div>
        </div>

        {/* Plans de Backup */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-red-600" />
            Plans de Backup Actifs
          </h3>
          <div className="space-y-4">
            {mockBackupPlans.map((backup, _index) => (
              <div key={backup.id} className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-800">{backup.name}</h4>
                    <p className="text-sm text-gray-600">{backup.frequency} • {backup.retention}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    backup.status === 'success' ? 'bg-green-100 text-green-800' :
                    backup.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {backup.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-medium">{backup.type}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Localisation:</span>
                    <span className="ml-2 font-medium">{backup.location}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Dernier backup:</span>
                    <span className="ml-2 font-medium">{backup.lastBackup}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Taille:</span>
                    <span className="ml-2 font-medium">{backup.size}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stratégie Récupération */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-lg border-l-4 border-red-500">
          <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" />
            Stratégie de Récupération
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Objectifs de Récupération</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• RTO (Recovery Time Objective): 4 heures</li>
                <li>• RPO (Recovery Point Objective): 1 heure</li>
                <li>• Tests de récupération: Mensuels</li>
                <li>• Documentation: À jour</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Procédures d'Urgence</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Escalade automatique</li>
                <li>• Équipe de réponse 24/7</li>
                <li>• Communication client</li>
                <li>• Post-mortem obligatoire</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function CatalogueActifsTI({ isOpen, onClose }: CatalogueActifsTIProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = createSlides();

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
